import axios from 'axios';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  'https://zeromiddle-api-1011312555956.asia-south1.run.app';

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('0middle_admin_token');

  if (token && config.url?.startsWith('/admin')) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Farmers
export const getFarmers = () => api.get('/farmers');

export const getFarmer = (farmerSlug) =>
  api.get(`/farmers/${farmerSlug}`);

// Products
export const getProducts = (farmerSlug) =>
  api.get('/products', {
    params: farmerSlug ? { farmer: farmerSlug } : {},
  });

export const getProduct = (productSlug) =>
  api.get(`/products/${productSlug}`);

export const getProductReviews = (productSlug) =>
  api.get(`/products/${productSlug}/reviews`);

export const createProductReview = (productSlug, review) =>
  api.post(`/products/${productSlug}/reviews`, review);

// Location helper
export const lookupPincode = (pincode) =>
  api.get(`/locations/pincode/${pincode}`);

// Orders
export const saveCheckoutDraft = (draft) => api.put('/orders/checkout-drafts', draft);
export const createOrder = (order) =>
  api.post('/orders', order);

export const getOrder = (orderId) =>
  api.get(`/orders/${orderId}`);

export const submitPayment = (orderId) =>
  api.post(`/orders/${orderId}/payment-submitted`, {});

// Anonymous customer funnel analytics
const getVisitorId = () => {
  const key = '0middle_visitor_id';
  let visitorId = localStorage.getItem(key) || sessionStorage.getItem(key);
  if (!visitorId) {
    visitorId =
      window.crypto?.randomUUID?.() ||
      `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  localStorage.setItem(key, visitorId);
  return visitorId;
};

export const trackAnalyticsEvent = (event, metadata = {}) =>
  api.post('/analytics/event', {
    visitor_id: getVisitorId(),
    event,
    path: window.location.pathname,
    metadata: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      language: navigator.language || null,
      screen:
        window.screen?.width && window.screen?.height
          ? `${window.screen.width}x${window.screen.height}`
          : null,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent || null,
      ...metadata,
    },
  });

// Admin
export const getAdminTestCommodities = () => api.get("/admin/test-requirements/commodities");
export const feedAdminTestRequirements = (commodity_url, product) => api.post("/admin/test-requirements/feed", { commodity_url, product });
export const adminLogin = (username, password) =>
  api.post('/admin/login', { username, password });

export const changeAdminPassword = (currentPassword, newPassword) =>
  api.post('/admin/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });

export const getAdminFarmers = () =>
  api.get('/admin/farmers');

export const createAdminFarmer = (farmer) =>
  api.post('/admin/farmers', farmer);

export const updateAdminFarmer = (farmerSlug, farmer) =>
  api.put(`/admin/farmers/${farmerSlug}`, farmer);

export const deleteAdminFarmer = (farmerSlug) =>
  api.delete(`/admin/farmers/${farmerSlug}`);

export const spellcheckAdminFarmer = (headline, story) =>
  api.post('/admin/farmers/spellcheck', { headline, story });

export const uploadAdminFarmerImage = (file, farmerHint) => {
  const form = new FormData();
  form.append('file', file);
  if (farmerHint) form.append('farmer_hint', farmerHint);
  return api.post('/admin/uploads/farmer-image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getAdminProducts = () =>
  api.get('/admin/products');

export const createAdminProduct = (product) =>
  api.post('/admin/products', product);

export const updateAdminProduct = (productSlug, product) =>
  api.put(`/admin/products/${productSlug}`, product);

export const copyAdminProduct = (productSlug) =>
  api.post(`/admin/products/${productSlug}/copy`);


export const uploadAdminProductImage = (file, productHint) => {
  const form = new FormData();
  form.append('file', file);
  if (productHint) form.append('product_hint', productHint);

  return api.post('/admin/uploads/product-image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const replaceAdminProductImage = (productSlug, file) => {
  const form = new FormData();
  form.append('file', file);

  return api.post(`/admin/products/${productSlug}/image`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const addAdminProductImage = (productSlug, file) => {
  const form = new FormData();
  form.append('file', file);

  return api.post(`/admin/products/${productSlug}/images`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const deleteAdminProductImageByUrl = (productSlug, imageUrl) =>
  api.delete(`/admin/products/${productSlug}/images`, {
    data: { image_url: imageUrl },
  });

export const setAdminPrimaryProductImage = (productSlug, imageUrl) =>
  api.put(`/admin/products/${productSlug}/primary-image`, {
    image_url: imageUrl,
  });

export const deleteUploadedProductImage = (objectKey) =>
  api.delete('/admin/uploads/product-image', {
    data: { object_key: objectKey },
  });

export const deleteAdminProductImage = (productSlug) =>
  api.delete(`/admin/products/${productSlug}/image`);

export const deleteAdminProduct = (productSlug) =>
  api.delete(`/admin/products/${productSlug}`);

export const addAdminInventory = (productSlug, payload) =>
  api.post(`/admin/products/${productSlug}/inventory`, payload);

export const updateAdminInventoryQuantity = (productSlug, quantity) =>
  api.patch(`/admin/products/${productSlug}/inventory/quantity`, { quantity });

export const getAdminInventoryProfile = () =>
  api.get('/admin/inventory-profile');

export const setAdminProductActive = (productSlug, active) =>
  api.patch(`/admin/products/${productSlug}/active`, { active });

export const askAdminOperationsAssistant = (question, history = []) =>
  api.post('/admin/operations-assistant', { question, history });

export const createAdminOrder = (payload) => api.post('/admin/orders/manual-create', payload);
export const editAdminOrder = (id, payload) => api.patch(`/admin/orders/${id}/edit`, payload);

export const getAdminOrders = (status) =>
  api.get('/admin/orders', {
    params: status ? { status } : {},
  });

export const deleteAdminOrder = (orderId) =>
  api.delete(`/admin/orders/${orderId}`);

export const confirmAdminPayment = (orderId) =>
  api.post(`/admin/orders/${orderId}/confirm-payment`);

export const unconfirmAdminPayment = (orderId) =>
  api.post(`/admin/orders/${orderId}/unconfirm-payment`);

export const updateAdminOrderStatus = (orderId, status) =>
  api.patch(`/admin/orders/${orderId}/status`, { status });

export const updateAdminOrderTracker = (orderId, payload) =>
  api.patch(`/admin/orders/${orderId}/tracker`, payload);

export const getAdminLedger = () =>
  api.get('/admin/ledger');

export const getAdminSummary = () =>
  api.get('/admin/summary');

export const getAdminAuditTrail = () =>
  api.get('/admin/audit-trail');

export const clearAdminAuditTrail = () =>
  api.delete('/admin/audit-trail');

export const getAdminCustomerFunnel = () =>
  api.get('/admin/customer-funnel');

export const getAdminWebTraffic = () =>
  api.get('/admin/web-traffic');

export const getAdminReviews = () =>
  api.get('/admin/reviews');

export const moderateAdminReview = (reviewId, status) =>
  api.patch(`/admin/reviews/${reviewId}`, { status });

export const chatAdminTestRequirements = (commodity_url, product, question, history = []) =>
  api.post('/admin/test-requirements/chat', { commodity_url, product, question, history });

export const getProductTestingRows = (product) => api.post('/admin/test-requirements/product-tests', { product });

export const askStandaloneTestingAssistant = (question, history = []) =>
  api.post('/admin/test-requirements/assistant', { question, history });

export default api;

export const getAdminProductDrafts = () => api.get('/admin/product-drafts');
export const saveAdminProductDraft = (form, draft_id = null) =>
  api.post('/admin/product-drafts', { form, draft_id });
export const deleteAdminProductDraft = (draftId) =>
  api.delete(`/admin/product-drafts/${draftId}`);
